# Overview

AI App Design (`aiapp-ui`) is a React component library for conversation-native UIs. It provides designed, customizable components that render inside chat messages -- cards, tables, carousels, approval flows, and more -- driven by structured data from your AI tools.

## Where it fits

Your design system gives you buttons, dialogs, and inputs. AI App Design gives you components purpose-built for chat: inline surfaces that display tool results, collect user decisions, and record what happened.

```
Radix / Tailwind (primitives) → aiapp-ui (conversation components) → Your LLM stack (orchestration)
```

AI App Design doesn't replace your design system. It extends it. Components use Radix and Tailwind internally and follow your theme.

## How it works

Every component has a corresponding schema -- a Zod definition describing the data it needs. When a tool returns structured data that matches the schema, the component renders. When it doesn't, parsing fails safely and nothing breaks.

1. **Server**: Define a tool that returns structured data.
2. **Client**: Register the component renderer that maps tool names to components.
3. **Runtime**: The tool fires, data is validated, and the component appears in the conversation.

## Core principles

- **Conversation-native**: Components are designed for narrow widths, variable heights, and the scroll rhythm of chat.
- **Schema-first**: Every surface is driven by a serializable schema with stable IDs. No brittle HTML strings.
- **Accessible**: Keyboard navigation, focus management, and ARIA roles throughout.
- **Customizable**: Built on Tailwind. Override styles, swap colors, and match your brand without forking.

## Next steps

- [Quick Start](/docs/quick-start) -- install and render your first component
- [Design Guidelines](/docs/design-guidelines) -- constraints and patterns for chat UI
- [Components](/docs/approval-card) -- browse the component library
